import {FC, useEffect, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {Crew} from '../core/_models'
import {CrewOvertime} from '../core/_requests'
import {
  blurNumberInputOnWheel,
  preventNumberInputArrowStep,
} from '../../../shared/utils/numberInputGuards'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type Props = {
  visible: boolean
  overtime: CrewOvertime | null
  isNew: boolean
  isSaving: boolean
  crewMembers: Crew[]
  lockCrewSelection?: boolean
  canEdit: boolean
  canSubmit?: boolean
  onClose: () => void
  onChange: (next: CrewOvertime) => void
  onCrewSelected: (crewId: number) => void
  onSave: () => void
  onSubmit: () => void
  crewSignOnDateById?: Map<number, string>
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const formatCurrency = (value: number) =>
  Number(value || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})

const resolveCurrencyCode = (
  salaryCurrency?: string | null,
  salaryOtherCurrency?: string | null
) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
          .formatToParts(0)
          .find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const CrewOvertimeModal: FC<Props> = ({
  visible,
  overtime,
  isNew,
  isSaving,
  crewMembers,
  lockCrewSelection = false,
  canEdit,
  canSubmit = false,
  onClose,
  onChange,
  onCrewSelected,
  onSave,
  onSubmit,
  crewSignOnDateById = new Map<number, string>(),
}) => {
  const [totalHoursInput, setTotalHoursInput] = useState('')

  useEffect(() => {
    if (!visible || !overtime) return
    const threshold = Math.max(Number(overtime.maxOtAllowed ?? 109), 0)
    const currentOtHours = Math.max(Number(overtime.otHours ?? 0), 0)
    setTotalHoursInput(String(currentOtHours + threshold))
  }, [visible, overtime?.id])

  if (!visible || !overtime) return null


  const currencySymbol = getCurrencySymbol(
    resolveCurrencyCode(overtime.salaryCurrency, overtime.salaryOtherCurrency)
  )

  const updateField = <K extends keyof CrewOvertime>(field: K, value: CrewOvertime[K]) => {
    onChange({...overtime, [field]: value})
  }

  return (
    <div className='modal-overlay' onClick={onClose}>
      <div
        className='modal-content'
        onClick={(e) => e.stopPropagation()}
        style={{maxWidth: '980px', maxHeight: '860px'}}
      >
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>{isNew ? 'Add Crew Overtime' : 'Edit Crew Overtime'}</h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body' style={{maxHeight: '68vh', overflowY: 'auto'}}>
          <div className='row g-4'>
            <div className='col-md-4'>
              <label className={LABEL}>
                Crew Member <span className='text-danger'>*</span>
              </label>
              {isNew && !lockCrewSelection ? (
                <SearchableSelect
                  className='form-select'
                  value={overtime.crewId || ''}
                  onChange={(e) => onCrewSelected(Number(e.target.value))}
                  disabled={isSaving}
                >
                  <option value=''>Select Crew Member</option>
                  {crewMembers.map((crew) => (
                    <option key={crew.id} value={crew.id}>
                      {crew.name}
                    </option>
                  ))}
                </SearchableSelect>
              ) : (
                <input className='form-control' value={overtime.crewName || ''} disabled />
              )}
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>Rank</label>
              <input className='form-control' value={overtime.rankName || ''} disabled />
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>
                Month <span className='text-danger'>*</span>
              </label>
              <input
                type='month'
                className='form-control'
                value={overtime.overtimeMonth ? overtime.overtimeMonth.slice(0, 7) : ''}
                onChange={(e) => {
                  const nextMonth = e.target.value
                  updateField('overtimeMonth', nextMonth ? `${nextMonth}-01` : '')
                }}
                disabled={!canEdit || isSaving}
              />
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>
                OT Hours <span className='text-danger'>*</span>
              </label>
              <input
                type='number'
                className='form-control'
                min={Number(overtime.maxOtAllowed ?? 109) + 1}
                step='0.01'
                value={totalHoursInput}
                onWheel={blurNumberInputOnWheel}
                onKeyDown={preventNumberInputArrowStep}
                onChange={(e) => {
                  const raw = e.target.value
                  setTotalHoursInput(raw)
                  const threshold = Math.max(Number(overtime.maxOtAllowed ?? 109), 0)
                  const parsed = Number(raw)
                  if (raw === '' || Number.isNaN(parsed)) {
                    updateField('otHours', 0)
                    return
                  }

                  updateField('otHours', Math.max(parsed - threshold, 0))
                }}
                onBlur={() => {
                  const threshold = Math.max(Number(overtime.maxOtAllowed ?? 109), 0)
                  const minimumAllowed = threshold + 1
                  const parsed = Number(totalHoursInput)
                  const normalized = Number.isFinite(parsed)
                    ? Math.max(parsed, minimumAllowed)
                    : minimumAllowed
                  setTotalHoursInput(String(normalized))
                  updateField('otHours', Math.max(normalized - threshold, 0))
                }}
                disabled={!canEdit || isSaving}
              />
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>Fixed OT</label>
              <input
                type='number'
                className='form-control'
                min={0}
                step='0.01'
                value={overtime.maxOtAllowed ?? 109}
                disabled
              />
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>Overtime Rate</label>
              <div className='input-group'>
                <span className='input-group-text'>{currencySymbol}</span>
                <input
                  type='number'
                  className='form-control'
                  min={0}
                  step='0.01'
                  value={overtime.overtimeRate ?? overtime.otPerHour ?? 0}
                  disabled
                />
              </div>
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>OT Per Month</label>
              <input
                className='form-control'
                value={`${currencySymbol}${formatCurrency(overtime.otPerMonth || 0)}`}
                disabled
              />
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>OT Per Hour</label>
              <input
                className='form-control'
                value={`${currencySymbol}${formatCurrency(overtime.otPerHour || 0)}`}
                disabled
              />
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>OT Amount</label>
              <input
                className='form-control'
                value={`${currencySymbol}${formatCurrency(overtime.otAmount || 0)}`}
                disabled
              />
            </div>

            <div className='col-md-12'>
              <label className={LABEL}>Remarks</label>
              <textarea
                className='form-control'
                rows={3}
                value={overtime.remarks || ''}
                onChange={(e) => updateField('remarks', e.target.value)}
                disabled={!canEdit || isSaving}
              />
            </div>
          </div>
        </div>

        <div className='custom-modal-footer d-flex justify-content-end gap-2'>
          <button
            type='button'
            className='btn btn-light btn-sm'
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn btn_primary btn-sm'
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isNew ? 'Create' : 'Save'}
          </button>
          {canSubmit ? (
            <button
              type='button'
              className='btn btn-success btn-sm'
              onClick={onSubmit}
              disabled={isSaving}
            >
              {isSaving ? 'Submitting...' : 'Submit'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default CrewOvertimeModal
